import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { v4 } from 'uuid'
import { windowStartingSize } from '../../../pages/desktop/desktop.constants'
import { axisI, openedWindow, windowI } from './WindowSlice.types'

const initialState: windowI = {
  openedWindows: [],
  windowsOrder: [],
  isChangingWindow: false
}

export const windowSlice = createSlice({
  name: 'window',
  initialState,
  reducers: {
    openWindow: (
      state,
      {
        payload
      }: PayloadAction<
        Omit<
          openedWindow,
          | 'windowLatestAxis'
          | 'windowLatestSize'
          | 'id'
          | 'minimized'
          | 'maximized'
        > &
          Partial<
            Pick<
              openedWindow,
              | 'windowLatestAxis'
              | 'windowLatestSize'
              | 'id'
              | 'minimized'
              | 'maximized'
            >
          >
      >
    ) => {
      const windowCenterXAxis =
        window.innerWidth / 2 - windowStartingSize.width / 2
      const windowCenterYAxis =
        window.innerHeight / 2 - windowStartingSize.height / 2
      const startingAxis: axisI = {
        x: windowCenterXAxis,
        y: windowCenterYAxis
      }
      const id = v4()
      const newOpenedWindow: openedWindow = {
        id: payload.id ?? id,
        windowLatestAxis: payload.windowLatestAxis ?? startingAxis,
        windowLatestSize: payload.windowLatestSize ?? {
          width: windowStartingSize.width,
          height: windowStartingSize.height
        },
        maximized: payload.maximized === undefined ? false : payload.maximized,
        minimized: payload.minimized === undefined ? false : payload.minimized,
        ...payload
      }
      state.openedWindows.push(newOpenedWindow)
      state.windowsOrder.push(payload.id ?? id)
    },
    closeAllWindows: (state) => {
      state.openedWindows = []
      state.windowsOrder = []
    },
    closeWindow: (state, { payload }: PayloadAction<{ id: string }>) => {
      const filteredOpenedWindows = state.openedWindows.filter(
        (window) => window.id !== payload.id
      )
      const filteredWindowsOrder = state.windowsOrder.filter(
        (appId) => appId !== payload.id
      )
      state.windowsOrder = filteredWindowsOrder
      state.openedWindows = filteredOpenedWindows
    },
    minimizeWindow: (state, { payload }: PayloadAction<{ id: string }>) => {
      const windowToMinimize = state.openedWindows.find(
        (window) => window.id === payload.id
      )
      if (windowToMinimize != null) windowToMinimize.minimized = true
    },
    unminimizeWindow: (state, { payload }: PayloadAction<{ id: string }>) => {
      const windowToUnMinimize = state.openedWindows.find(
        (window) => window.id === payload.id
      )
      if (windowToUnMinimize != null) windowToUnMinimize.minimized = false
    },
    maximizeWindow: (state, { payload }: PayloadAction<{ id: string }>) => {
      const windowToMaximize = state.openedWindows.find(
        (window) => window.id === payload.id
      )
      if (windowToMaximize != null) windowToMaximize.maximized = true
    },
    unMaximizeWindow: (state, { payload }: PayloadAction<{ id: string }>) => {
      const windowToUnMaximize = state.openedWindows.find(
        (window) => window.id === payload.id
      )
      if (windowToUnMaximize != null) windowToUnMaximize.maximized = false
    },
    moveWindowToTop: (state, { payload }: PayloadAction<{ id: string }>) => {
      const windowToMoveIndex = state.windowsOrder.indexOf(payload.id)
      const windowIdToMove = state.windowsOrder[windowToMoveIndex]
      if (windowIdToMove) {
        state.windowsOrder.splice(windowToMoveIndex, 1)
        state.windowsOrder.push(windowIdToMove)
      }
    },
    changeWindowLatestAxis: (
      state,
      { payload }: PayloadAction<{ id: string; x: number; y: number }>
    ) => {
      const windowToChange = state.openedWindows.find(
        (window) => window.id === payload.id
      )
      if (windowToChange != null) {
        windowToChange.windowLatestAxis = {
          x: payload.x,
          y: payload.y
        }
      }
    },
    changeWindowLatestSize: (
      state,
      { payload }: PayloadAction<{ id: string; height: number; width: number }>
    ) => {
      const windowToChange = state.openedWindows.find(
        (window) => window.id === payload.id
      )
      if (windowToChange != null) {
        windowToChange.windowLatestSize = {
          height: payload.height,
          width: payload.width
        }
      }
    },
    setIsChangingWindow: (
      state,
      { payload }: PayloadAction<{ isChangingWindow: boolean }>
    ) => {
      state.isChangingWindow = payload.isChangingWindow
    },
    minimizeAll: (state) => {
      state.openedWindows.map((window) => (window.minimized = true))
    }
  }
})

export const {
  openWindow,
  closeWindow,
  minimizeWindow,
  unminimizeWindow,
  maximizeWindow,
  unMaximizeWindow,
  moveWindowToTop,
  changeWindowLatestAxis,
  changeWindowLatestSize,
  minimizeAll,
  setIsChangingWindow,
  closeAllWindows
} = windowSlice.actions
